import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, FloppyDisk, Sparkle } from "@phosphor-icons/react";
import { getRecipe, remixRecipe, NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS, MINERAL_KEYS, VITAMIN_KEYS } from "../lib/api";

const UNITS = ["g","kg","mg","oz","lb","ml","l","cup","tbsp","tsp","piece","slice","clove"];

function fmt(v, d = 1) {
  if (v == null || Number.isNaN(v)) return "0";
  const n = Number(v);
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(d);
}

export default function RecipeRemix() {
  const { id } = useParams();
  const nav = useNavigate();
  const [original, setOriginal] = useState(null);
  const [rows, setRows] = useState([]); // {rid, name, quantity, unit}
  const [name, setName] = useState("");
  const [servings, setServings] = useState(1);
  const [remixed, setRemixed] = useState(null);
  const [diff, setDiff] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getRecipe(id);
        setOriginal(r);
        setName(`${r.name} · Remix`);
        setServings(r.servings);
        setRows(r.ingredients.map((i) => ({
          rid: crypto.randomUUID(),
          name: i.name, quantity: i.quantity, unit: i.unit,
        })));
      } catch (e) { toast.error("Failed to load recipe"); }
    })();
  }, [id]);

  const compute = useCallback(async (persist = false) => {
    if (!id) return;
    const payload = rows.filter(r => r.name && Number(r.quantity) > 0)
      .map(r => ({ name: r.name, quantity: Number(r.quantity), unit: r.unit }));
    if (!payload.length) return;
    if (persist) setSaving(true); else setLoading(true);
    try {
      const d = await remixRecipe(id, name, Number(servings) || 1, payload, persist);
      setRemixed(d.remixed); setDiff(d.diff);
      if (persist) {
        toast.success("Remix saved");
        nav(`/recipe/${d.remixed.id}`);
      }
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); setSaving(false); }
  }, [id, name, servings, rows, nav]);

  // Auto-compute on any change (debounced)
  useEffect(() => {
    if (!rows.length) return;
    const t = setTimeout(() => compute(false), 400);
    return () => clearTimeout(t);
  }, [rows, servings, compute]);

  const upd = (rid, key, val) => setRows(prev => prev.map(r => r.rid === rid ? { ...r, [key]: val } : r));
  const rm = (rid) => setRows(prev => prev.filter(r => r.rid !== rid));
  const add = () => setRows(prev => [...prev, { rid: crypto.randomUUID(), name: "", quantity: 0, unit: "g" }]);

  if (!original) return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 font-mono text-sm text-muted-foreground">Loading original…</div>
  );

  const orig = original.per_serving || {};
  const rem = remixed?.per_serving || {};

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="remix-page">
      <div className="pb-6 border-b border-border">
        <Link to={`/recipe/${id}`} data-testid="back-orig" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
          <ArrowLeft size={14} /> Back to {original.name}
        </Link>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Recipe remix</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Tweak & compare</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Swap, add, or resize ingredients. The nutrient shift shows live vs. the original per-serving values.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="space-y-3" data-testid="remix-editor">
          <div className="grid grid-cols-[1fr_140px_auto] gap-2">
            <input value={name} onChange={e => setName(e.target.value)}
              data-testid="remix-name"
              className="h-11 px-3 border border-border bg-background font-display font-medium text-lg focus:outline-none focus:border-primary transition-colors duration-200" />
            <input type="number" min="1" value={servings} onChange={e => setServings(e.target.value)}
              data-testid="remix-servings"
              className="h-11 px-3 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200" />
            <button onClick={() => compute(true)} disabled={saving} data-testid="remix-save"
              className="h-11 px-4 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2 transition-colors duration-200">
              <FloppyDisk size={14} /> Save
            </button>
          </div>

          <div className="grid grid-cols-[1fr_100px_100px_36px] gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground pb-1 border-b border-border">
            <div>Ingredient</div><div className="text-right">Qty</div><div>Unit</div><div />
          </div>
          {rows.map((r) => (
            <div key={r.rid} className="grid grid-cols-[1fr_100px_100px_36px] gap-2" data-testid={`remix-row-${r.rid}`}>
              <input value={r.name} onChange={e => upd(r.rid, "name", e.target.value)}
                className="h-10 px-3 border border-border bg-background focus:outline-none focus:border-primary transition-colors duration-200" />
              <input type="number" value={r.quantity} min="0" onChange={e => upd(r.rid, "quantity", e.target.value)}
                className="h-10 px-3 border border-border bg-background font-mono text-right focus:outline-none focus:border-primary transition-colors duration-200" />
              <select value={r.unit} onChange={e => upd(r.rid, "unit", e.target.value)}
                className="h-10 px-2 border border-border bg-background font-mono text-sm focus:outline-none focus:border-primary transition-colors duration-200">
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <button onClick={() => rm(r.rid)}
                className="h-10 border border-border hover:bg-muted flex items-center justify-center transition-colors duration-200"><X size={12} /></button>
            </div>
          ))}
          <button onClick={add}
            className="h-10 px-4 border border-dashed border-border hover:border-foreground hover:bg-muted flex items-center gap-2 text-sm transition-colors duration-200">
            <Plus size={14} /> Add ingredient
          </button>
        </div>

        <div className="border border-border" data-testid="remix-diff">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Live per-serving diff</div>
              <div className="text-sm font-medium mt-0.5">Remix vs Original</div>
            </div>
            {loading && <Sparkle size={14} className="animate-pulse text-primary" />}
          </div>
          <div className="p-4 max-h-[600px] overflow-y-auto space-y-1.5">
            {[...MACRO_KEYS, ...MINERAL_KEYS, ...VITAMIN_KEYS].map(k => {
              const o = Number(orig[k] || 0);
              const n = Number(rem[k] || 0);
              const dif = Number(diff[k] || 0);
              const pctChange = o ? Math.round(((n - o) / o) * 100) : (n > 0 ? 100 : 0);
              const positive = dif > 0.01, negative = dif < -0.01;
              const isLimitNutrient = ["sodium","sugar","saturated_fat","cholesterol","calories"].includes(k);
              const goodDir = isLimitNutrient ? negative : positive;
              const badDir = isLimitNutrient ? positive : negative;
              const color = goodDir ? "text-accent" : badDir ? "text-primary" : "text-muted-foreground";
              return (
                <div key={k} className="grid grid-cols-[1fr_auto_auto] gap-3 items-baseline py-1 border-b border-border last:border-b-0" data-testid={`diff-${k}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{NUTRIENT_LABELS[k]}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    <span className="line-through opacity-60">{fmt(o)}</span> → <span className="font-medium text-foreground">{fmt(n)}</span> {NUTRIENT_UNITS[k]}
                  </span>
                  <span className={`font-mono text-[10px] font-medium w-14 text-right ${color}`}>
                    {dif > 0 ? "+" : ""}{fmt(dif)} ({pctChange >= 0 ? "+" : ""}{pctChange}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
